import { useState } from 'react';
import { FiX, FiAlertTriangle, FiSend, FiCornerUpLeft } from 'react-icons/fi';

const ModalEnviarFlujo = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  datoTema, 
  estadoActual,
  siguienteRol,
  modoMasivo = false,
  cantidadSeleccionados = 0,
  esDevolucion = false
}) => {
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEnviar = async () => {
    if (!motivo.trim()) {
      alert('Por favor, ingresa un motivo para enviar este dato');
      return;
    }

    setLoading(true);
    try {
      await onConfirm(motivo);
      setMotivo('');
    } catch (error) {
      console.error('Error al enviar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setMotivo('');
      onClose();
    }
  };

  if (!isOpen) return null;

  const getRolInfo = () => {
    const roles = {
      'pendiente': { siguiente: 'Validador', accion: 'revisión inicial' },
      'creada_gestor': { siguiente: 'Validador', accion: 'revisión y validación' },
      'informacion_valida': { siguiente: 'Editor', accion: 'revisión final y aprobación' }
    };
    return roles[estadoActual] || { siguiente: 'Siguiente rol', accion: 'revisión' };
  };

  const rolInfo = getRolInfo();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-full">
              <FiAlertTriangle className="text-yellow-600" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {esDevolucion ? 'Confirmar Devolución' : 'Confirmar Envío'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Dato a enviar */}
          {modoMasivo ? (
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-blue-300">
              <p className="text-sm text-gray-600 mb-1">📦 Envío Masivo:</p>
              <p className="font-bold text-blue-900 text-lg">{cantidadSeleccionados} registros seleccionados</p>
              <p className="text-xs text-gray-500 mt-1">El motivo se aplicará a todos los registros</p>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Dato histórico:</p>
              <p className="font-medium text-gray-900">{datoTema}</p>
            </div>
          )}

          {/* Información del envío */}
          <div className={`${esDevolucion ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4`}>
            <p className={`text-sm ${esDevolucion ? 'text-yellow-900' : 'text-blue-900'} font-medium mb-2`}>
              {esDevolucion ? '↩️ Acción a realizar:' : '📤 Acción a realizar:'}
            </p>
            <p className={`text-sm ${esDevolucion ? 'text-yellow-800' : 'text-blue-800'}`}>
              Al aceptar, se {esDevolucion ? 'devolverá' : 'enviará'} el registro {esDevolucion ? 'al rol anterior' : 'para ' + rolInfo.accion} por el rol:{' '}
              <strong className={esDevolucion ? 'text-yellow-900' : 'text-blue-900'}>{siguienteRol}</strong>
            </p>
          </div>

          {/* Advertencia */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <FiAlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm text-red-900 font-medium">
                  Esta acción no se puede deshacer
                </p>
                <p className="text-xs text-red-700 mt-1">
                  Una vez {esDevolucion ? 'devuelto' : 'enviado'}, el dato saldrá de tu bandeja y será asignado al {siguienteRol}.
                </p>
              </div>
            </div>
          </div>

          {/* Campo de motivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo {esDevolucion ? 'de la devolución' : 'del envío'} *
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={esDevolucion 
                ? "Explica qué correcciones o cambios son necesarios..."
                : "Describe brevemente por qué estás enviando este dato al siguiente estado..."
              }
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Este motivo será registrado en el historial del dato.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleEnviar}
            disabled={loading || !motivo.trim()}
            className={`px-4 py-2 ${esDevolucion ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'} text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{esDevolucion ? 'Devolviendo...' : 'Enviando...'}</span>
              </>
            ) : (
              <>
                {esDevolucion ? <FiCornerUpLeft /> : <FiSend />}
                <span>{esDevolucion ? 'Devolver' : 'Enviar'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalEnviarFlujo;

